/* eslint-disable */
import React from "react";
import { useTranslation } from "react-i18next";
import * as url from "helper/UrlHelper";

// ─── Payload preview builder (same logic as ScheduleAutomationPopup) ─────────
const buildPayloadPreview = (automationConfig, isReactive, apiBaseUrl) => {
  if (!automationConfig) return null;
  const extra =
    automationConfig.payload_template &&
    typeof automationConfig.payload_template === "object" &&
    Object.keys(automationConfig.payload_template).length > 0
      ? automationConfig.payload_template
      : {};
  if (isReactive) {
    return {
      callbackUrl: `${apiBaseUrl}/automationtaskschedule/webhook/n8n/callback`,
      taskid: "{{taskid}}",
      email: "{{email}}",
      name: "{{name}}",
      phone: "{{phone}}",
      token: automationConfig.token || "",
      webhook_name: automationConfig.webhook_name || "",
      ...extra,
    };
  }
  return { taskid: "{{taskid}}", email: "{{email}}", name: "{{name}}", phone: "{{phone}}", ...extra };
};

// ─────────────────────────────────────────────────────────────────────────────
// AutomationTemplateDetailPopup
// ─────────────────────────────────────────────────────────────────────────────
const AutomationTemplateDetailPopup = ({ template, isOpen, onClose }) => {
  const { t } = useTranslation();

  if (!isOpen || !template) return null;

  const isReactive = template.executiontype === "reactive";
  const subtasks = [...(template.subtaskblueprint || [])].sort(
    (a, b) => (a.step || 0) - (b.step || 0)
  );

  return (
    <>
      <div
        className="offcanvas offcanvas-end bg-white border-0 shadow-lg show"
        style={{ visibility: "visible", width: "80vw", maxWidth: "960px" }}
        tabIndex="-1"
        aria-labelledby="offcanvasTemplateDetailLabel"
      >
        {/* ── Header ── */}
        <div className="offcanvas-header bg-primary text-white p-4 border-bottom">
          <div className="d-flex align-items-center gap-3">
            <div
              className="p-2 rounded-circle d-flex align-items-center justify-content-center"
              style={{ background: "rgba(255,255,255,0.15)" }}
            >
              <span className="material-symbols-outlined text-white" style={{ fontSize: "22px" }}>
                folder_special
              </span>
            </div>
            <div>
              <h5 className="offcanvas-title fw-bold text-white mb-1" id="offcanvasTemplateDetailLabel">
                {template.name}
              </h5>
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}>
                  {isReactive ? t("Reactive") : t("Proactive")}
                </span>
                <span className={`badge ${template.ispublic ? "bg-success" : "bg-secondary"}`}>
                  {template.ispublic ? t("Public") : t("Private")}
                </span>
                <small className="text-white opacity-75">
                  {subtasks.length} {t("subtask blueprints")}
                </small>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={onClose}
            aria-label="Close"
          />
        </div>

        {/* ── Body ── */}
        <div className="offcanvas-body p-4" style={{ overflowY: "auto" }}>

          {/* ── 1. Template Info ── */}
          <div className="mb-4 p-4 rounded-3 border border-primary border-opacity-25 shadow-sm bg-light">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>
                info
              </span>
              <h6 className="mb-0 fw-bold text-dark">{t("Template Info")}</h6>
            </div>

            <h5 className="fw-bold text-dark mb-1">{template.name}</h5>
            {template.description && (
              <p className="text-muted mb-3" style={{ fontSize: "14px", lineHeight: "1.6" }}>
                {template.description}
              </p>
            )}

            <div className="row g-3">
              <div className="col-md-3 col-6">
                <small className="text-muted d-flex align-items-center gap-1 mb-1">
                  <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>bolt</span>
                  {t("Execution Type")}
                </small>
                <span className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}>
                  {isReactive ? t("Reactive") : t("Proactive")}
                </span>
              </div>

              <div className="col-md-3 col-6">
                <small className="text-muted d-flex align-items-center gap-1 mb-1">
                  <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>visibility</span>
                  {t("Visibility")}
                </small>
                <span className={`badge ${template.ispublic ? "bg-success" : "bg-secondary"}`}>
                  {template.ispublic ? t("Public") : t("Private")}
                </span>
              </div>

              <div className="col-md-3 col-6">
                <small className="text-muted d-flex align-items-center gap-1 mb-1">
                  <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>check_circle</span>
                  {t("Status")}
                </small>
                <span className={`badge ${template.status === "active" ? "bg-success" : "bg-danger"}`}>
                  {template.status || "active"}
                </span>
              </div>

              <div className="col-md-3 col-6">
                <small className="text-muted d-flex align-items-center gap-1 mb-1">
                  <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>calendar_today</span>
                  {t("Created")}
                </small>
                <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                  {new Date(template.createdAt).toLocaleDateString()}
                </span>
              </div>

              {template.owner && (
                <div className="col-md-6 col-12">
                  <small className="text-muted d-flex align-items-center gap-1 mb-1">
                    <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>person</span>
                    {t("Owner")}
                  </small>
                  <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                    {template.owner.name} {template.owner.surname}
                    {template.owner.email && (
                      <span className="text-muted fw-normal ms-1">({template.owner.email})</span>
                    )}
                  </span>
                </div>
              )}

              {template.process && (
                <div className="col-md-6 col-12">
                  <small className="text-muted d-flex align-items-center gap-1 mb-1">
                    <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>settings</span>
                    {t("Process ID")}
                  </small>
                  <code style={{ fontSize: "12px", color: "#555" }}>{template.process}</code>
                </div>
              )}
            </div>
          </div>

          {/* ── 2. Task Blueprint ── */}
          {template.taskblueprint?.title && (
            <div className="mb-4 p-4 rounded-3 border border-primary shadow-sm bg-white">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>
                  account_tree
                </span>
                <h6 className="mb-0 fw-bold text-dark">{t("Parent Task Blueprint")}</h6>
                <span className="badge bg-primary ms-auto">{t("Parent Task")}</span>
              </div>
              <h6 className="fw-semibold text-dark mb-1">{template.taskblueprint.title}</h6>
              {template.taskblueprint.description && (
                <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                  {template.taskblueprint.description}
                </p>
              )}
            </div>
          )}

          {/* ── 3. Subtask Blueprints ── */}
          {subtasks.length > 0 && (
            <div>
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>
                  list_alt
                </span>
                <h6 className="mb-0 fw-bold text-dark">{t("Subtask Blueprints")}</h6>
                <span className="badge bg-secondary ms-auto">{subtasks.length} {t("subtasks")}</span>
              </div>

              {subtasks.map((st, idx) => {
                const isReactiveSt = isReactive;
                const tplProactiveUrl = `${url.API_BASE_URL}/automationtaskschedule/proactive?email={user_email}`;
                const payloadStr =
                  typeof st.automation?.payload_template === "object"
                    ? JSON.stringify(st.automation?.payload_template || {})
                    : (st.automation?.payload_template || "");
                const previewData = buildPayloadPreview(
                  { ...st.automation, payload_template: st.automation?.payload_template || {} },
                  isReactiveSt,
                  url.API_BASE_URL
                );

                return (
                  <div
                    key={st._id || idx}
                    className="mb-4 border rounded-3 bg-white shadow-sm overflow-hidden"
                  >
                    {/* Subtask header */}
                    <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light border-bottom">
                      <span className="badge bg-primary">#{idx + 1}</span>
                      <span className="badge bg-secondary">{t("Step")} {st.step || idx + 1}</span>
                      <span className={`badge ${isReactiveSt ? "bg-info text-dark" : "bg-warning text-dark"}`}>
                        {isReactiveSt ? t("Reactive") : t("Proactive")}
                      </span>
                      <span className="fw-semibold text-dark ms-1">{st.title}</span>
                    </div>

                    <div className="p-3">
                      {st.description && (
                        <p className="text-muted fs-sm mb-3">{st.description}</p>
                      )}

                      {/* Automation config - read-only */}
                      <div className="border rounded-3 overflow-hidden">
                        <div
                          className="px-3 py-2 border-bottom d-flex align-items-center gap-2"
                          style={{ background: isReactiveSt ? "rgba(13,202,240,0.08)" : "rgba(255,193,7,0.08)" }}
                        >
                          <span className="material-symbols-outlined text-secondary" style={{ fontSize: "16px" }}>
                            settings_ethernet
                          </span>
                          <span className="fw-semibold fs-sm text-dark">
                            {isReactiveSt ? t("Webhook Config") : t("Payload Config")}
                          </span>
                          <span
                            className={`badge ms-auto ${isReactiveSt ? "bg-info text-dark" : "bg-warning text-dark"}`}
                            style={{ fontSize: "10px" }}
                          >
                            {isReactiveSt ? "POST" : "GET"}
                          </span>
                          <span className="badge bg-secondary" style={{ fontSize: "10px" }}>
                            {t("read-only")}
                          </span>
                        </div>

                        <div className="row g-0">
                          {/* Left: fields */}
                          <div className="col-xl-6 p-3 border-end">
                            {isReactiveSt && (
                              <>
                                <div className="mb-3">
                                  <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                                    <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>key</span>
                                    {t("Token")}
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm font-monospace bg-light"
                                    value={st.automation?.token || "—"}
                                    readOnly
                                    style={{ fontSize: "11px", color: "#555" }}
                                  />
                                </div>
                                <div className="mb-3">
                                  <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                                    <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>link</span>
                                    {t("Webhook URL")}
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm bg-light"
                                    value={st.automation?.webhook_url || "—"}
                                    readOnly
                                    style={{ fontSize: "12px", color: "#555" }}
                                  />
                                </div>
                                {st.automation?.webhook_name && (
                                  <div className="mb-3">
                                    <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>label</span>
                                      {t("Webhook Name")}
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm bg-light"
                                      value={st.automation.webhook_name}
                                      readOnly
                                      style={{ fontSize: "12px", color: "#555" }}
                                    />
                                  </div>
                                )}
                              </>
                            )}

                            {/* Proactive endpoint (shown for proactive templates) */}
                            {!isReactiveSt && (
                              <div className="mb-3 p-2 rounded-2 border" style={{ background: "#fff8e1" }}>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                  <span className="material-symbols-outlined text-warning" style={{ fontSize: "16px" }}>api</span>
                                  <small className="fw-semibold text-dark">{t("n8n CRON polls this endpoint:")}</small>
                                  <span className="badge bg-warning text-dark ms-auto" style={{ fontSize: "9px" }}>GET</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <code
                                    className="flex-grow-1 p-2 rounded-2 border"
                                    style={{
                                      background: "#1e1e2e",
                                      color: "#cdd6f4",
                                      fontSize: "11px",
                                      wordBreak: "break-all",
                                      display: "block",
                                      lineHeight: "1.7",
                                    }}
                                  >
                                    {tplProactiveUrl}
                                  </code>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary flex-shrink-0"
                                    title={t("Copy URL")}
                                    onClick={() => navigator.clipboard?.writeText(tplProactiveUrl)}
                                  >
                                    <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>content_copy</span>
                                  </button>
                                </div>
                                <small className="text-muted d-block mt-1" style={{ fontSize: "10px" }}>
                                  {t("Replace {user_email} with the actual moderator email.")}
                                </small>
                              </div>
                            )}

                            {/* Payload */}
                            <div>
                              <label className="form-label fs-sm fw-semibold text-dark mb-1">
                                {isReactiveSt ? t("Extra Payload Fields") : t("Payload")}
                              </label>
                              {payloadStr && payloadStr !== "{}" ? (
                                <pre
                                  className="rounded-2 p-2 mb-0 bg-light font-monospace"
                                  style={{ fontSize: "11px", color: "#555", maxHeight: "120px", overflowY: "auto" }}
                                >
                                  {typeof st.automation?.payload_template === "object"
                                    ? JSON.stringify(st.automation?.payload_template, null, 2)
                                    : (st.automation?.payload_template || "")}
                                </pre>
                              ) : (
                                <p className="text-muted fs-sm mb-0 fst-italic">{t("No extra fields")}</p>
                              )}
                            </div>
                          </div>

                          {/* Right: payload preview */}
                          <div className="col-xl-6 p-3 d-flex flex-column" style={{ background: "#fafafa" }}>
                            <label className="form-label fs-sm fw-semibold d-flex align-items-center gap-2 mb-2">
                              <span className="material-symbols-outlined text-primary" style={{ fontSize: "15px" }}>preview</span>
                              {t("Payload Preview")}
                              <span className="badge bg-primary ms-1" style={{ fontSize: "10px" }}>
                                {isReactiveSt ? "POST" : "GET"}
                              </span>
                            </label>
                            {previewData ? (
                              <pre
                                className="rounded-3 p-3 mb-2 flex-grow-1"
                                style={{
                                  background: "#1e1e2e",
                                  color: "#cdd6f4",
                                  fontSize: "11px",
                                  lineHeight: "1.6",
                                  maxHeight: "250px",
                                  overflowY: "auto",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-all",
                                }}
                              >
                                {JSON.stringify(previewData, null, 2)}
                              </pre>
                            ) : (
                              <div className="text-center py-3 text-muted">
                                <small>{t("No payload configured")}</small>
                              </div>
                            )}
                            <small className="text-muted d-flex align-items-center gap-1 mt-1">
                              <span className="material-symbols-outlined" style={{ fontSize: "12px" }}>info</span>
                              <code>{"{{taskid}}"}</code>{", "}
                              <code>{"{{email}}"}</code>{" "}
                              {t("replaced with real lead data.")}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {subtasks.length === 0 && !template.taskblueprint?.title && (
            <div className="text-center py-5">
              <span className="material-symbols-outlined text-muted d-block" style={{ fontSize: "48px" }}>
                inbox
              </span>
              <p className="text-muted mt-2">{t("No blueprints configured for this template.")}</p>
            </div>
          )}

        </div>
      </div>

      {/* Backdrop */}
      <div className="offcanvas-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default AutomationTemplateDetailPopup;
